import * as THREE from 'three';

import {
    at,
    Backdrop,
    ball,
    box,
    cone,
    createKit,
    cyl,
    flat,
    glow,
    matte as toon,
    plain as inked,
    radialTexture,
    rng,
    screenToRoot,
    skyDome,
    starField,
} from './common';

const SATURN_COLORS = ['#b8793c', '#d99a52', '#f0c27a', '#fbe3b0', '#e8b468'];
const JUPITER_COLORS = [
    '#8c5a3c',
    '#c9a37a',
    '#f1e4cc',
    '#b98b5e',
    '#e8d5b5',
    '#a8764a',
];
const NEPTUNE_COLORS = ['#2540a8', '#2f55d4', '#3b64e0', '#2a49c0'];

/**
 * A gas giant's body: soft, wavy bands that blend into each other, darker toward the poles.
 * `bands` multiplies the stripe frequency; `spot` paints Jupiter's Great Red Spot.
 */
const gasGiantTexture = (
    colors: string[],
    bands = 1,
    spot = false
): THREE.CanvasTexture => {
    const W = 256;
    const H = 512;
    const c = document.createElement('canvas');
    c.width = W;
    c.height = H;
    const g = c.getContext('2d');
    if (g) {
        const palette = colors.map((h) => new THREE.Color(h));
        const img = g.createImageData(W, H);
        const col = new THREE.Color();
        for (let y = 0; y < H; y++) {
            for (let x = 0; x < W; x++) {
                const u = (x / W) * Math.PI * 2;
                // Wavy band edges; integer x frequencies keep the texture seamless
                const yy =
                    y +
                    Math.sin(u * 3 + y * 0.05) * 4 +
                    Math.sin(u * 7 + y * 0.13) * 1.5;
                const band =
                    0.5 +
                    0.5 *
                        (Math.sin(yy * 0.045 * bands) * 0.5 +
                            Math.sin(yy * 0.11 * bands + 1.3) * 0.3 +
                            Math.sin(yy * 0.27 * bands + 0.4) * 0.2);
                const f =
                    Math.min(0.999, Math.max(0, band)) * (palette.length - 1);
                const i = Math.floor(f);
                col.copy(palette[i]).lerp(palette[i + 1], f - i);
                const shade = 0.72 + 0.28 * Math.sin((Math.PI * y) / H);
                const p = (y * W + x) * 4;
                img.data[p] = Math.min(255, col.r * 255 * shade);
                img.data[p + 1] = Math.min(255, col.g * 255 * shade);
                img.data[p + 2] = Math.min(255, col.b * 255 * shade);
                img.data[p + 3] = 255;
            }
        }
        g.putImageData(img, 0, 0);
        if (spot) {
            // An oval storm in the southern bands, with a pale wake around it
            const x = W * 0.62;
            const y = H * 0.62;
            g.fillStyle = 'rgba(240,222,190,0.55)';
            g.beginPath();
            g.ellipse(x, y, 40, 24, 0, 0, Math.PI * 2);
            g.fill();
            g.fillStyle = '#b4452c';
            g.beginPath();
            g.ellipse(x, y, 30, 16, 0, 0, Math.PI * 2);
            g.fill();
            g.fillStyle = '#cf6a45';
            g.beginPath();
            g.ellipse(x - 3, y - 2, 15, 7, 0, 0, Math.PI * 2);
            g.fill();
        }
    }
    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
};

/** A rocky or cloudy surface: a base colour with soft dark and light blotches, optional ice caps. */
const rockyTexture = (
    base: string,
    dark: string,
    light: string,
    seed: number,
    opts: { caps?: boolean; blotch?: number } = {}
): THREE.CanvasTexture => {
    const r = rng(seed);
    const W = 512;
    const H = 256;
    const c = document.createElement('canvas');
    c.width = W;
    c.height = H;
    const g = c.getContext('2d');
    if (g) {
        g.fillStyle = base;
        g.fillRect(0, 0, W, H);
        const size = opts.blotch ?? 40;
        // Drawn one canvas-width to each side so nothing is cut at the seam
        const blotches = (color: string, n: number, alpha: number) => {
            g.fillStyle = color;
            g.globalAlpha = alpha;
            for (let i = 0; i < n; i++) {
                const x = r() * W;
                const y = H * 0.12 + r() * H * 0.76;
                const rx = size * (0.4 + r() * 0.9);
                const ry = rx * (0.35 + r() * 0.35);
                [-W, 0, W].forEach((dx) => {
                    g.beginPath();
                    g.ellipse(x + dx, y, rx, ry, r() * Math.PI, 0, Math.PI * 2);
                    g.fill();
                });
            }
            g.globalAlpha = 1;
        };
        blotches(dark, 16, 0.55);
        blotches(light, 12, 0.45);
        if (opts.caps) {
            g.fillStyle = '#f4f8ff';
            g.beginPath();
            g.ellipse(W / 2, 0, W, H * 0.1, 0, 0, Math.PI * 2);
            g.fill();
            g.beginPath();
            g.ellipse(W / 2, H, W, H * 0.08, 0, 0, Math.PI * 2);
            g.fill();
        }
    }
    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
};

/** Saturn's rings across one radius: many fine bands, a faint inner ring and the dark Cassini gap. */
const ringTexture = (): THREE.CanvasTexture => {
    const W = 1024;
    const c = document.createElement('canvas');
    c.width = W;
    c.height = 4;
    const g = c.getContext('2d');
    if (g) {
        const r = rng(41);
        let x = 0;
        while (x < W) {
            const t = x / W;
            const w = 2 + r() * 9;
            let a = 0.55 + r() * 0.4;
            let tone = 0.78 + r() * 0.22;
            if (t < 0.12)
                a *= 0.3; // faint inner ring
            else if (t < 0.4) tone *= 0.85; // the thinner, dimmer B ring edge
            if (t > 0.62 && t < 0.69) a = 0.04; // Cassini division
            if (t > 0.93) a *= 0.5; // fading outer edge
            const k = Math.round(tone * 255);
            g.fillStyle = `rgba(${k},${Math.round(k * 0.86)},${Math.round(k * 0.62)},${a})`;
            g.fillRect(x, 0, w, 4);
            x += w;
        }
    }
    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
};

const vhash = (x: number, y: number, z: number) => {
    const s = Math.sin(x * 127.1 + y * 311.7 + z * 74.7) * 43758.5453;
    return s - Math.floor(s);
};

/** Smooth 3D value noise, 0..1. */
const vnoise = (x: number, y: number, z: number) => {
    const ix = Math.floor(x);
    const iy = Math.floor(y);
    const iz = Math.floor(z);
    const sm = (f: number) => f * f * (3 - 2 * f);
    const fx = sm(x - ix);
    const fy = sm(y - iy);
    const fz = sm(z - iz);
    const c = (dx: number, dy: number, dz: number) =>
        vhash(ix + dx, iy + dy, iz + dz);
    const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
    return lerp(
        lerp(
            lerp(c(0, 0, 0), c(1, 0, 0), fx),
            lerp(c(0, 1, 0), c(1, 1, 0), fx),
            fy
        ),
        lerp(
            lerp(c(0, 0, 1), c(1, 0, 1), fx),
            lerp(c(0, 1, 1), c(1, 1, 1), fx),
            fy
        ),
        fz
    );
};

/** Layered noise, 0..1. */
const fbm = (x: number, y: number, z: number, octaves: number) => {
    let sum = 0;
    let amp = 0.5;
    let f = 1;
    let norm = 0;
    for (let i = 0; i < octaves; i++) {
        sum += amp * vnoise(x * f, y * f, z * f);
        norm += amp;
        f *= 2.03;
        amp *= 0.5;
    }
    return sum / norm;
};

/**
 * A detailed unit-radius rock: big lumps, medium bumps and fine grit, pitted with craters that have
 * raised rims, smooth analytic normals, and per-vertex colour that darkens crater floors and patches.
 */
const rockGeometry = (
    seed: number,
    tint: THREE.ColorRepresentation
): THREE.BufferGeometry => {
    const r = rng(seed * 97 + 3);
    const craters = Array.from({ length: 8 }, () => ({
        d: new THREE.Vector3(r() * 2 - 1, r() * 2 - 1, r() * 2 - 1).normalize(),
        rad: 0.16 + r() * 0.3,
        depth: 0.05 + r() * 0.09,
    }));
    const out = { rad: 1, shade: 1 };
    const sample = (d: THREE.Vector3) => {
        const sx = d.x + seed * 3.1;
        const sy = d.y + seed * 1.7;
        const sz = d.z - seed * 2.3;
        let h = (fbm(sx * 1.3, sy * 1.3, sz * 1.3, 3) - 0.5) * 0.6;
        h += (fbm(sx * 4, sy * 4, sz * 4, 3) - 0.5) * 0.24;
        h += (vnoise(sx * 14, sy * 14, sz * 14) - 0.5) * 0.06;
        let shade = 0.78 + fbm(sx * 3 + 9, sy * 3, sz * 3, 3) * 0.5;
        craters.forEach((c) => {
            const q = Math.acos(Math.min(1, Math.max(-1, d.dot(c.d)))) / c.rad;
            if (q < 1) {
                const bowl = 1 - q * q;
                h -= c.depth * bowl;
                shade *= 1 - 0.3 * bowl;
            } else if (q < 1.3) {
                const rim = Math.sin(((q - 1) / 0.3) * Math.PI);
                h += c.depth * 0.4 * rim;
                shade *= 1 + 0.14 * rim;
            }
        });
        out.rad = 1 + h;
        out.shade = shade;
    };

    const geo = new THREE.IcosahedronGeometry(1, 9);
    const pos = geo.attributes.position;
    const nor = geo.attributes.normal;
    const col = new Float32Array(pos.count * 3);
    const base = new THREE.Color(tint);
    // Polyhedron corners are duplicated per face: work each direction out once
    const cache = new Map<string, number[]>();
    const d = new THREE.Vector3();
    const dA = new THREE.Vector3();
    const dB = new THREE.Vector3();
    const t1 = new THREE.Vector3();
    const t2 = new THREE.Vector3();
    const p0 = new THREE.Vector3();
    const p1 = new THREE.Vector3();
    const p2 = new THREE.Vector3();
    const n = new THREE.Vector3();
    const eps = 0.012;
    for (let i = 0; i < pos.count; i++) {
        d.fromBufferAttribute(pos, i).normalize();
        const key = `${Math.round(d.x * 4096)},${Math.round(d.y * 4096)},${Math.round(d.z * 4096)}`;
        let e = cache.get(key);
        if (!e) {
            sample(d);
            const shade = out.shade;
            p0.copy(d).multiplyScalar(out.rad);
            t1.crossVectors(
                d,
                Math.abs(d.y) < 0.9
                    ? new THREE.Vector3(0, 1, 0)
                    : new THREE.Vector3(1, 0, 0)
            ).normalize();
            t2.crossVectors(d, t1);
            dA.copy(d).addScaledVector(t1, eps).normalize();
            sample(dA);
            p1.copy(dA).multiplyScalar(out.rad);
            dB.copy(d).addScaledVector(t2, eps).normalize();
            sample(dB);
            p2.copy(dB).multiplyScalar(out.rad);
            n.crossVectors(p1.sub(p0), p2.sub(p0)).normalize();
            if (n.dot(d) < 0) n.negate();
            e = [p0.x, p0.y, p0.z, n.x, n.y, n.z, shade];
            cache.set(key, e);
        }
        pos.setXYZ(i, e[0], e[1], e[2]);
        nor.setXYZ(i, e[3], e[4], e[5]);
        col[i * 3] = base.r * e[6];
        col[i * 3 + 1] = base.g * e[6];
        col[i * 3 + 2] = base.b * e[6];
    }
    geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
    return geo;
};

/** An earth-like world: ocean, green blobs, white clouds. */
const worldTexture = (seed: number): THREE.CanvasTexture => {
    const r = rng(seed);
    const W = 1024;
    const H = 512;
    const c = document.createElement('canvas');
    c.width = W;
    c.height = H;
    const g = c.getContext('2d');
    if (g) {
        // Ocean, a touch lighter toward the equator
        const sea = g.createLinearGradient(0, 0, 0, H);
        sea.addColorStop(0, '#1f63c4');
        sea.addColorStop(0.5, '#2f86e6');
        sea.addColorStop(1, '#1f63c4');
        g.fillStyle = sea;
        g.fillRect(0, 0, W, H);
        // Every shape is drawn again one canvas-width to each side so nothing is cut at the seam
        const blobAt = (
            x: number,
            y: number,
            rx: number,
            ry: number,
            a: number
        ) =>
            [-W, 0, W].forEach((dx) => {
                g.beginPath();
                g.ellipse(x + dx, y, rx, ry, a, 0, Math.PI * 2);
                g.fill();
            });
        const land = (color: string, n: number, size: number) => {
            g.fillStyle = color;
            for (let i = 0; i < n; i++) {
                const x = r() * W;
                // Keep land off the stretched polar rows
                const y = H * 0.2 + r() * H * 0.6;
                for (let k = 0; k < 7; k++) {
                    blobAt(
                        x + (r() - 0.5) * size * 1.4,
                        y + (r() - 0.5) * size * 0.7,
                        size * (0.25 + r() * 0.35),
                        size * (0.15 + r() * 0.25),
                        r() * Math.PI
                    );
                }
            }
        };
        land('#4fb85a', 6, 90);
        land('#c9b26a', 3, 34);
        // Polar caps: wide ellipses so the pinched rows read as ice
        g.fillStyle = '#f4f8ff';
        blobAt(W / 2, 0, W, H * 0.09, 0);
        blobAt(W / 2, H, W, H * 0.09, 0);
        // Soft clouds
        g.fillStyle = 'rgba(255,255,255,0.7)';
        for (let i = 0; i < 12; i++) {
            const x = r() * W;
            const y = H * 0.15 + r() * H * 0.7;
            for (let k = 0; k < 4; k++) {
                blobAt(
                    x + (r() - 0.5) * 70,
                    y + (r() - 0.5) * 14,
                    26 + r() * 30,
                    6 + r() * 7,
                    (r() - 0.5) * 0.3
                );
            }
        }
    }
    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
};

const craterMoon = (
    radius: number,
    color: number,
    seed: number
): THREE.Group => {
    const r = rng(seed);
    const g = new THREE.Group();
    g.add(inked(ball(toon(color), radius), 0.025));
    const crater = toon(new THREE.Color(color).multiplyScalar(0.72).getHex());
    for (let i = 0; i < 9; i++) {
        const dir = new THREE.Vector3(
            r() * 2 - 1,
            r() * 2 - 1,
            r() * 1.2 + 0.1
        ).normalize();
        const size = radius * (0.1 + r() * 0.16);
        const disc = new THREE.Mesh(new THREE.CircleGeometry(size, 14), crater);
        disc.position.copy(dir).multiplyScalar(radius * 1.002);
        disc.lookAt(dir.clone().multiplyScalar(radius * 2));
        g.add(disc);
        const rim = new THREE.Mesh(
            new THREE.RingGeometry(size * 0.98, size * 1.18, 14),
            toon(new THREE.Color(color).multiplyScalar(1.1).getHex())
        );
        rim.position.copy(dir).multiplyScalar(radius * 1.003);
        rim.lookAt(dir.clone().multiplyScalar(radius * 2));
        g.add(rim);
    }
    return g;
};

const ufo = (): { group: THREE.Group; lights: THREE.Mesh[] } => {
    const g = new THREE.Group();
    const metal = toon(0xb9c2d6);
    g.add(inked(ball(metal, 2.4, 0.55, 2.4), 0.05));
    const dome = ball(
        toon(0x8fe3ff, { transparent: true, opacity: 0.85 }),
        1.15,
        0.9,
        1.15
    );
    dome.position.y = 0.45;
    g.add(inked(dome, 0.06));
    // A tiny tomato pilot, naturally
    g.add(at(ball(toon(0xe63a2a), 0.4), 0, 0.5, 0));
    g.add(at(cone3(0x2fae45), 0, 0.92, 0));
    const lights: THREE.Mesh[] = [];
    const cols = [0xff5d73, 0xffd23f, 0x2ec4b6, 0x3a86ff, 0xff5d73, 0xffd23f];
    cols.forEach((c, i) => {
        const a = (i / cols.length) * Math.PI * 2;
        const l = ball(flat(c), 0.2);
        l.position.set(Math.cos(a) * 2.1, -0.05, Math.sin(a) * 2.1);
        g.add(l);
        lights.push(l);
    });
    return { group: g, lights };
};

const cone3 = (color: number): THREE.Mesh =>
    new THREE.Mesh(new THREE.ConeGeometry(0.18, 0.3, 5), toon(color));

/** Solar cells: dark blue squares divided by thin silver busbars. */
const solarTexture = (): THREE.CanvasTexture => {
    const c = document.createElement('canvas');
    c.width = 256;
    c.height = 128;
    const g = c.getContext('2d');
    if (g) {
        const grad = g.createLinearGradient(0, 0, 256, 128);
        grad.addColorStop(0, '#1d3c8f');
        grad.addColorStop(0.5, '#27479f');
        grad.addColorStop(1, '#162f78');
        g.fillStyle = grad;
        g.fillRect(0, 0, 256, 128);
        g.strokeStyle = '#a9b8d8';
        g.lineWidth = 2;
        for (let x = 0; x <= 256; x += 32) {
            g.beginPath();
            g.moveTo(x, 0);
            g.lineTo(x, 128);
            g.stroke();
        }
        for (let y = 0; y <= 128; y += 32) {
            g.beginPath();
            g.moveTo(0, y);
            g.lineTo(256, y);
            g.stroke();
        }
        // Finer cell lines inside each cell
        g.strokeStyle = 'rgba(160,185,235,0.35)';
        g.lineWidth = 1;
        for (let x = 8; x < 256; x += 16) {
            g.beginPath();
            g.moveTo(x, 0);
            g.lineTo(x, 128);
            g.stroke();
        }
    }
    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
};

/** A communications / radar satellite: foil-wrapped bus, jointed solar wings, dish and antennas. */
const satellite = (): THREE.Group => {
    const g = new THREE.Group();
    const std = (
        color: number,
        metalness: number,
        roughness: number,
        extra: THREE.MeshStandardMaterialParameters = {}
    ) =>
        new THREE.MeshStandardMaterial({
            color,
            metalness,
            roughness,
            ...extra,
        });
    const foil = std(0xd9a93a, 0.85, 0.32);
    const foilDark = std(0xa57a22, 0.8, 0.4);
    const alu = std(0xcfd5df, 0.8, 0.3);
    const dark = std(0x23262e, 0.4, 0.6);
    const white = std(0xf1f3f7, 0.25, 0.45, { side: THREE.DoubleSide });

    // Bus, with a recessed band, radiator panels and thrusters
    g.add(box(foil, 1.2, 1.0, 1.5));
    g.add(at(box(foilDark, 1.26, 0.12, 1.56), 0, 0.2, 0));
    [-1, 1].forEach((s) => {
        g.add(at(box(alu, 0.04, 0.6, 0.9), s * 0.62, -0.05, 0));
        g.add(at(cyl(dark, 0.07, 0.14), s * 0.4, -0.55, -0.6));
    });
    const sensor = cyl(dark, 0.17, 0.28);
    sensor.position.set(0, 0.62, 0.45);
    g.add(sensor);
    g.add(at(ball(std(0x6aa6ff, 0.9, 0.1), 0.12), 0, 0.78, 0.45));

    // Solar wings: a boom, then three hinged panels each side
    const cells = solarTexture();
    const panelMat = std(0xffffff, 0.55, 0.28, {
        map: cells,
        emissive: 0x0a1a44,
        emissiveIntensity: 0.6,
        side: THREE.DoubleSide,
    });
    [-1, 1].forEach((s) => {
        const boom = cyl(alu, 0.05, 0.9);
        boom.rotation.z = Math.PI / 2;
        boom.position.x = s * 1.05;
        g.add(boom);
        for (let i = 0; i < 3; i++) {
            const x = s * (1.55 + i * 1.5);
            g.add(at(box(panelMat, 1.4, 0.04, 1.9), x, 0, 0));
            // Thin frame and hinge between segments
            g.add(at(box(alu, 1.44, 0.03, 0.05), x, 0.005, 0.97));
            g.add(at(box(alu, 1.44, 0.03, 0.05), x, 0.005, -0.97));
            g.add(at(box(alu, 0.08, 0.05, 0.12), x - s * 0.75, 0, 0));
        }
    });

    // Parabolic dish on a short mast, feed horn on three struts
    const profile: THREE.Vector2[] = [];
    for (let i = 0; i <= 14; i++) {
        const r = (i / 14) * 0.95;
        profile.push(new THREE.Vector2(r, r * r * 0.85));
    }
    const dishGeo = new THREE.LatheGeometry(profile, 36);
    const dish = new THREE.Group();
    dish.add(new THREE.Mesh(dishGeo, white));
    [0, 1, 2].forEach((i) => {
        const a = (i / 3) * Math.PI * 2 + 0.5;
        const strut = cyl(alu, 0.012, 1.0);
        strut.position.set(Math.cos(a) * 0.35, 0.58, Math.sin(a) * 0.35);
        strut.rotation.set(-Math.sin(a) * 0.36, 0, Math.cos(a) * 0.36);
        dish.add(strut);
    });
    const horn = cone(dark, 0.07, 0.18);
    horn.position.y = 1.02;
    horn.rotation.x = Math.PI;
    dish.add(horn);
    dish.position.set(0, 0.55, -0.95);
    dish.rotation.x = -1.05;
    g.add(at(cyl(alu, 0.04, 0.5), 0, 0.5, -0.65));
    g.add(dish);

    // Whip antennas
    g.add(at(cyl(alu, 0.012, 1.1), 0.45, 0.95, -0.4));
    g.add(at(cyl(alu, 0.012, 0.8), -0.45, 0.8, -0.2));
    return g;
};

export const buildSpace = (dark: boolean): Backdrop => {
    // Space is always night; light mode just gets a bluer, brighter sky.
    const kit = createKit({
        sky: dark ? 0x8a8fd8 : 0xa8b8ff,
        ground: 0x2a2060,
        hemi: dark ? 1.2 : 1.5,
        sun: 0xfff3dc,
        sunIntensity: 2.8,
        sunPos: [-8, 6, 8],
    });
    const { scene, root, ticks } = kit;
    const r = rng(17);

    // Everything is placed by where it should land on screen (-1..1 each way, y up), at a depth, and
    // `fit` turns that into positions for the view's actual shape. The tomato sits left-of-centre-up,
    // so the slots ring it: corners and edges, not the middle.
    const slots: {
        obj: THREE.Object3D;
        nx: number;
        ny: number;
        z: number;
        scale: THREE.Vector3;
    }[] = [];
    const place = <T extends THREE.Object3D>(
        obj: T,
        nx: number,
        ny: number,
        z: number
    ): T => {
        root.add(obj);
        slots.push({ obj, nx, ny, z, scale: obj.scale.clone() });
        return obj;
    };

    scene.add(
        dark
            ? skyDome(0x03020f, 0x0d0830, 0x241255)
            : skyDome(0x0b1450, 0x2a2f94, 0x5a4cc0)
    );

    // Stars: a dense fine layer and a sparse bright one that twinkles out of step
    const fine = starField(900, 380, 1, { size: 1.8 });
    const bright = starField(160, 370, 2, { size: 3.6 });
    const twinkle = starField(120, 360, 3, { size: 3.2 });
    scene.add(fine, bright, twinkle);
    ticks.push((t) => {
        (bright.material as THREE.PointsMaterial).opacity =
            0.65 + Math.sin(t * 2.1) * 0.35;
        (twinkle.material as THREE.PointsMaterial).opacity =
            0.65 + Math.sin(t * 2.1 + 3.1) * 0.35;
    });

    // Nebula clouds
    [
        { c: 'rgba(255,60,160,1)', x: -90, y: 70, z: -300, s: 260, o: 0.45 },
        { c: 'rgba(60,200,255,1)', x: 110, y: 110, z: -320, s: 300, o: 0.4 },
        { c: 'rgba(140,90,255,1)', x: 20, y: 30, z: -310, s: 340, o: 0.35 },
        { c: 'rgba(255,140,60,1)', x: -150, y: 150, z: -330, s: 220, o: 0.28 },
    ].forEach(({ c, x, y, z, s, o }) => {
        const n = glow(c, s, o);
        n.position.set(x, y, z);
        root.add(n);
    });

    // A ringed gas giant
    const giant = new THREE.Group();
    giant.add(
        ball(toon(0xffffff, { map: gasGiantTexture(SATURN_COLORS) }), 17)
    );
    // A faint haze so the limb is soft
    giant.add(ball(toon(0xffd9a0, { transparent: true, opacity: 0.12 }), 17.5));
    const ringGeo = new THREE.RingGeometry(22, 41, 128, 1);
    const ringUv = ringGeo.attributes.uv;
    const ringPos = ringGeo.attributes.position;
    for (let i = 0; i < ringPos.count; i++) {
        const rad = Math.hypot(ringPos.getX(i), ringPos.getY(i));
        ringUv.setXY(i, (rad - 22) / (41 - 22), 0.5);
    }
    const ring = new THREE.Mesh(
        ringGeo,
        toon(0xffffff, {
            map: ringTexture(),
            side: THREE.DoubleSide,
            transparent: true,
            depthWrite: false,
        })
    );
    ring.rotation.x = Math.PI / 2 - 0.42;
    giant.add(ring);
    giant.rotation.z = 0.28;
    place(giant, -0.6, 0.5, -190);

    // The Sun, far off in the corner it lights everything from
    const sun = new THREE.Group();
    sun.add(ball(flat(0xfff1b0), 38));
    const corona = glow('rgba(255,200,90,1)', 260, 0.85);
    const halo = glow('rgba(255,150,60,1)', 480, 0.4);
    sun.add(corona, halo);
    place(sun, -0.85, 0.85, -330);

    // The rest of the family, roughly in order of distance from the Sun
    const planet = (
        radius: number,
        map: THREE.Texture,
        nx: number,
        ny: number,
        z: number
    ) => place(inked(ball(toon(0xffffff, { map }), radius), 0.03), nx, ny, z);
    const mercury = planet(
        2,
        rockyTexture('#9a948f', '#6e6965', '#c4beb8', 5, { blotch: 22 }),
        -0.72,
        0.02,
        -110
    );
    const venus = planet(
        4.6,
        rockyTexture('#e6c88a', '#d2ab6a', '#f6e6bd', 6, { blotch: 70 }),
        -0.2,
        0.9,
        -240
    );
    const mars = planet(
        4,
        rockyTexture('#c1573a', '#8f3b26', '#e09a6c', 12, { caps: true }),
        0.62,
        0.3,
        -190
    );
    const jupiter = planet(
        16,
        gasGiantTexture(JUPITER_COLORS, 1.6, true),
        0.7,
        0.74,
        -260
    );
    // Bands run level across the view, with the same slight axial tilt as Saturn
    jupiter.rotation.order = 'ZYX';
    jupiter.rotation.z = 0.12;
    const uranus = planet(
        6.5,
        rockyTexture('#a9e4e8', '#8fd0d6', '#cdf4f4', 8, { blotch: 90 }),
        0.2,
        0.92,
        -300
    );
    // Thin rings seen from slightly above, like Saturn's
    const uranusRing = new THREE.Mesh(
        new THREE.RingGeometry(9, 11.5, 64),
        toon(0xbfeff2, {
            transparent: true,
            opacity: 0.55,
            side: THREE.DoubleSide,
            depthWrite: false,
        })
    );
    uranusRing.rotation.set(Math.PI / 2 - 0.35, 0, 0.1);
    uranus.add(uranusRing);
    const neptune = planet(
        6,
        gasGiantTexture(NEPTUNE_COLORS, 0.8),
        0.86,
        -0.05,
        -330
    );

    // A blue-green world with a moon
    const world = new THREE.Group();
    const globe = inked(
        ball(toon(0xffffff, { map: worldTexture(9) }), 9),
        0.025
    );
    world.add(globe);
    world.add(ball(toon(0xbfe6ff, { transparent: true, opacity: 0.18 }), 9.6));
    const moonlet = craterMoon(2.2, 0xd8d4e6, 4);
    world.add(moonlet);
    place(world, 0.55, -0.5, -125);

    // A big cratered moon, low and close
    const moon = craterMoon(8, 0xc8c6dd, 21);
    place(moon, -0.6, -0.5, -62);

    // An asteroid belt of detailed rocks, scattered round the edges of the view
    const rockMat = toon(0xffffff, { vertexColors: true });
    const rockGeos = [0x9d8f86, 0x8a7a8f, 0xb09a82, 0x7f8794].map((c, i) =>
        rockGeometry(i + 1, c)
    );
    const rocks: {
        m: THREE.Mesh;
        spin: THREE.Vector3;
        drift: number;
        base: number;
    }[] = [];
    for (let i = 0; i < 16; i++) {
        // Try a few spots: outside the tomato's own area and clear of everything already placed
        let nx = 0;
        let ny = 0;
        for (let tries = 0; tries < 40; tries++) {
            nx = r() * 2 - 1;
            ny = r() * 2 - 1;
            const onTomato = Math.abs(nx) < 0.42 && ny > -0.2 && ny < 0.8;
            const crowded = slots.some(
                (s) => Math.hypot(s.nx - nx, s.ny - ny) < 0.24
            );
            if (!onTomato && !crowded) break;
        }
        const z = -(70 + r() * 170);
        // Farther rocks are bigger, so they read as rocks and not specks
        const size = (1.4 + r() * 2.6) * (-z / 120);
        const m = inked(new THREE.Mesh(rockGeos[i % 4], rockMat), 0.05);
        m.scale.set(
            size * (1 + r() * 0.4),
            size * (0.75 + r() * 0.35),
            size * (0.85 + r() * 0.3)
        );
        m.rotation.set(r() * 6, r() * 6, r() * 6);
        place(m, nx, ny, z);
        rocks.push({
            m,
            spin: new THREE.Vector3(
                r() - 0.5,
                r() - 0.5,
                r() - 0.5
            ).multiplyScalar(0.4),
            drift: 0.3 + r() * 0.5,
            base: 0,
        });
    }
    ticks.push((t, dt) => {
        rocks.forEach((rock, i) => {
            rock.m.rotation.x += rock.spin.x * dt;
            rock.m.rotation.y += rock.spin.y * dt;
            rock.m.position.y = rock.base + Math.sin(t * rock.drift + i) * 1.6;
        });
        giant.rotation.y = t * 0.02;
        world.children[0].rotation.y = t * 0.08;
        moonlet.position.set(
            Math.cos(t * 0.3) * 14,
            Math.sin(t * 0.3) * 2,
            Math.sin(t * 0.3) * 14
        );
        mercury.rotation.y = t * 0.02;
        venus.rotation.y = -t * 0.015;
        mars.rotation.y = t * 0.05;
        jupiter.rotation.y = t * 0.09;
        uranus.rotation.y = t * 0.04;
        neptune.rotation.y = t * 0.04;
        const pulse = 1 + Math.sin(t * 0.7) * 0.03;
        corona.scale.set(260 * pulse, 260 * pulse, 1);
        halo.scale.set(480 / pulse, 480 / pulse, 1);
        moon.rotation.y = t * 0.03;
    });

    // The satellite, low on the right
    const satPivot = place(new THREE.Group(), 0.82, -0.22, -26);
    const sat = satellite();
    sat.scale.setScalar(0.85);
    satPivot.add(sat);
    ticks.push((t) => {
        sat.position.set(Math.sin(t * 0.25) * 2.5, Math.cos(t * 0.3) * 1.2, 0);
        sat.rotation.set(0.3, t * 0.25, 0.15);
    });

    // A flying saucer that crosses every so often, edge to edge of whatever the view is
    const lane = { x0: -78, x1: 78, y: 18, z: -52 };
    const saucer = ufo();
    root.add(saucer.group);
    ticks.push((t) => {
        const cycle = 34;
        const p = (t % cycle) / cycle;
        saucer.group.position.set(
            lane.x0 + p * (lane.x1 - lane.x0),
            lane.y + Math.sin(p * 18) * 1.8,
            lane.z - Math.sin(p * Math.PI) * 6
        );
        saucer.group.rotation.z = Math.sin(p * 18) * 0.12;
        saucer.group.rotation.y = t * 0.6;
        saucer.lights.forEach((l, i) => {
            (l.material as THREE.MeshBasicMaterial).color.setHSL(
                (t * 0.6 + i / 6) % 1,
                1,
                0.6
            );
        });
    });

    // Comets: now and then one streaks across, each on its own slow schedule
    const sky = { halfW: 85, yLo: 22, yHi: 48 };
    const hash = (n: number) => {
        const x = Math.sin(n * 127.1 + 311.7) * 43758.5453;
        return x - Math.floor(x);
    };
    const tailTex = radialTexture([
        [0, 'rgba(190,235,255,0.9)'],
        [1, 'rgba(190,235,255,0)'],
    ]);
    const makeComet = () => {
        const g = new THREE.Group();
        g.add(ball(flat(0xffffff), 0.8));
        g.add(glow('rgba(190,235,255,1)', 9, 1));
        const tail = new THREE.Mesh(
            new THREE.ConeGeometry(0.7, 24, 10, 1, true),
            new THREE.MeshBasicMaterial({
                map: tailTex,
                transparent: true,
                blending: THREE.AdditiveBlending,
                depthWrite: false,
                toneMapped: false,
                side: THREE.DoubleSide,
            })
        );
        tail.rotation.z = -Math.PI / 2;
        tail.position.x = 12;
        g.add(tail);
        g.visible = false;
        root.add(g);
        return g;
    };
    const comets = [
        { g: makeComet(), cycle: 23, offset: 4, dur: 5.5 },
        { g: makeComet(), cycle: 37, offset: 17, dur: 6.5 },
        { g: makeComet(), cycle: 53, offset: 31, dur: 7 },
    ];
    ticks.push((t) => {
        comets.forEach((c, ci) => {
            const u = t + c.offset;
            const n = Math.floor(u / c.cycle);
            const local = u - n * c.cycle;
            c.g.visible = local < c.dur;
            if (!c.g.visible) return;
            const k = local / c.dur;
            const h = (j: number) => hash(n * 7.13 + ci * 3.7 + j);
            const dir = h(0) < 0.5 ? -1 : 1;
            const span = sky.yHi - sky.yLo;
            const y0 = sky.yLo + h(1) * span;
            // A little wider than the view so it enters and leaves off-screen
            const dx = sky.halfW * 2.5 * dir;
            const dy = -(0.15 + h(2) * 0.35) * span;
            const z = -(70 + h(3) * 40);
            c.g.position.set(-dx / 2 + k * dx, y0 + k * dy, z);
            c.g.rotation.z = Math.atan2(-dy, -dx);
        });
    });

    const backdrop = kit.finish(true);
    backdrop.orbit = true;
    const tmp = new THREE.Vector3();
    backdrop.fit = (camera) => {
        // Narrow (portrait) views get everything a bit smaller so it is not all cropped
        const k = THREE.MathUtils.clamp(0.4 + camera.aspect * 0.65, 0.6, 1);
        slots.forEach((s) => {
            screenToRoot(camera, root, s.nx, s.ny, s.z, s.obj.position);
            s.obj.scale.copy(s.scale).multiplyScalar(k);
        });
        rocks.forEach((rock) => {
            rock.base = rock.m.position.y;
        });
        screenToRoot(camera, root, -1.3, 0.6, lane.z, tmp);
        lane.x0 = tmp.x;
        lane.y = tmp.y;
        screenToRoot(camera, root, 1.3, 0.6, lane.z, tmp);
        lane.x1 = tmp.x;
        screenToRoot(camera, root, 1, 0.95, -90, tmp);
        sky.halfW = tmp.x;
        sky.yHi = tmp.y;
        screenToRoot(camera, root, 1, 0.3, -90, tmp);
        sky.yLo = tmp.y;
    };
    return backdrop;
};
